import { useState } from "react";
import axios from "axios";
import { MessageSquareText, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { CONTACT } from "../content";

export const TOPICS = ["General enquiry", "Counselling & mental health", "Allied health", "Children & families",
  "Family reports & court assessments", "Care navigation & assessments", "Parenting programs – expression of interest", "Professional services", "Funding & fees", "Other"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api/enquiries`;
const field = "mt-2 w-full rounded-2xl bg-[#F9F6EF] border border-[#3F4A3C]/15 px-4 py-3 font-jakarta text-sm text-[#3F4A3C] placeholder:text-[#5E6B57]/70 focus:outline-none focus:border-[#5F7C8A]";
const Label = ({ children }) => <span className="font-jakarta text-xs uppercase tracking-[0.2em] text-[#5E6B57]">{children}</span>;

const Sent = ({ onClose }) => (
  <div className="py-10 text-center" data-testid="enquiry-success">
    <span className="mx-auto w-14 h-14 rounded-full bg-[#E3E9E4] grid place-items-center"><Check className="w-6 h-6 text-[#3F4A3C]" /></span>
    <DialogTitle className="mt-6 font-newsreader font-normal text-3xl text-[#3F4A3C]">Thank you, we've got it.</DialogTitle>
    <DialogDescription className="mt-3 font-jakarta text-sm text-[#4A4F45] max-w-sm mx-auto">Our team will be in touch soon. If it's urgent, please call {CONTACT.phone}.</DialogDescription>
    <button onClick={onClose} data-testid="enquiry-done-btn" className="mt-8 rounded-full bg-[#3F4A3C] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] transition-colors">Close</button>
  </div>
);

export const EnquiryForm = ({ topic, onSent, initialMessage = "" }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", topic, message: initialMessage, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.post(API, { ...f, page: window.location.pathname });
      onSent();
    } catch (x) {
      const d = x?.response?.data?.detail;
      setErr(typeof d === "string" ? d : Array.isArray(d) ? "Please check your email address and try again." : `Something went wrong. Please call us on ${CONTACT.phone}.`);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="enquiry-form">
      <label className="block"><Label>Your name</Label><input required value={f.name} onChange={set("name")} data-testid="enquiry-name" className={field} /></label>
      <label className="block"><Label>Email</Label><input required type="email" value={f.email} onChange={set("email")} data-testid="enquiry-email" className={field} /></label>
      <label className="block"><Label>Phone (optional)</Label><input value={f.phone} onChange={set("phone")} data-testid="enquiry-phone" className={field} /></label>
      <label className="block"><Label>About</Label><select value={f.topic} onChange={set("topic")} data-testid="enquiry-topic" className={field}>{TOPICS.map((t) => <option key={t}>{t}</option>)}</select></label>
      <label className="block sm:col-span-2"><Label>How can we help?</Label><textarea required rows={4} value={f.message} onChange={set("message")} data-testid="enquiry-message" placeholder="A few lines is plenty. Please don't include detailed health information." className={field} /></label>
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden />
      {err && <p className="sm:col-span-2 font-jakarta text-sm text-[#9A4A3A]" data-testid="enquiry-error">{err}</p>}
      <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4">
        <p className="font-jakarta text-xs text-[#5E6B57] max-w-xs">Sent straight to our team at {CONTACT.email}.</p>
        <button disabled={busy} type="submit" data-testid="enquiry-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3F4A3C] px-7 py-3.5 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#5F7C8A] disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareText className="w-4 h-4" />} Send enquiry
        </button>
      </div>
    </form>
  );
};

export const EnquiryBtn = ({ className = "", testid = "enquiry-btn", topic = "General enquiry", label = "Send an enquiry" }) => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const change = (o) => { setOpen(o); if (!o) setSent(false); };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className={`inline-flex items-center gap-2 rounded-full border border-[#3F4A3C]/25 px-6 py-3.5 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors duration-500 ${className}`}>
        <MessageSquareText className="w-4 h-4" /> {label}
      </button>
      <Dialog open={open} onOpenChange={change}>
        <DialogContent data-lenis-prevent className="max-w-2xl w-[94vw] max-h-[90vh] overflow-y-auto rounded-[28px] border-0 bg-[#F6F2E9] p-6 sm:p-10" data-testid="enquiry-dialog">
          {sent ? <Sent onClose={() => change(false)} /> : (
            <>
              <DialogTitle className="font-newsreader font-normal text-3xl sm:text-4xl text-[#3F4A3C]">Send an enquiry</DialogTitle>
              <DialogDescription className="mt-2 mb-6 font-jakarta text-sm text-[#4A4F45]">Tell us a little about what you're looking for and we'll get back to you. Not sure what to ask? That's okay, we'll help you work it out.</DialogDescription>
              <EnquiryForm topic={topic} onSent={() => setSent(true)} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
